import { useEffect } from 'react'

import { useAppRouter as useRouter } from '@/shared/hooks/useAppRouter'

import { DateTime } from 'luxon'
import { useSession } from '@/shared/services/session/compat'
import { alert } from '@/shared/ui/dialog'

import { modules } from '@/modules'
import type { ModuleNavItem } from '@/modules'
import type { GamificationPage } from '@/types/gamificationPage'

import type { DateTime as LuxonDateTime } from 'luxon';

export interface UseLockProps {
  gamification_allowed_for?: string
  gamification_start_at?: string
  gamification_end_at?: string
  gamification_disallowed_modules?: string[]
  pages?: GamificationPage[]
}

export type LockStatus = 'open' | 'soon' | 'closed'

export type IsAllowedModuleFn = (slug: string) => boolean

export type UseLockResult = [
  isAllowedModule: IsAllowedModuleFn,
  byPass: boolean,
  status: LockStatus,
  startAt: LuxonDateTime,
  endAt: LuxonDateTime
]

export const useLock = (
  props: UseLockProps = {},
  basePathWithId: string
): UseLockResult => {
  const router = useRouter()

  const { digitalProductId } = router.query as { digitalProductId?: string }

  const {
    gamification_allowed_for = '',
    gamification_start_at,
    gamification_end_at,
    gamification_disallowed_modules = [],
    pages
  } = props

  const { data: session } = useSession()
  const user = (session?.user || {}) as { email?: string }
  const startAt = DateTime.fromISO(gamification_start_at ?? '')
  const endAt = DateTime.fromISO(gamification_end_at ?? '')
  const now = DateTime.local()
  const disallowedModules: string[] = gamification_disallowed_modules || []

  const byPass: boolean =
    !!gamification_allowed_for &&
    gamification_allowed_for.includes(user?.email ?? '')

  let status: LockStatus = 'open'
  if (now < startAt) status = 'soon'
  else if (endAt && endAt.isValid && now >= endAt) status = 'closed'

  const isAllowedModule: IsAllowedModuleFn = slug => {
    if (pages && pages.length > 0) {
      const page = pages.find(p => p.module_type === slug)
      return page ? !!page.active : true
    }
    return !disallowedModules.includes(slug)
  }

  useEffect(() => {
    const hasDisallowedModules = disallowedModules.length > 0

    if (hasDisallowedModules) {
      const currentModule = modules.find(
        (m: ModuleNavItem) =>
          !['/', ''].includes(m.href) &&
          (router.asPath.startsWith(m.href) ||
            router.asPath.includes(`${digitalProductId}${m.href}`))
      )

      if (currentModule && !isAllowedModule(currentModule.slug)) {
        router.push(`${basePathWithId}/`)

        void alert({
          variant: 'info',
          title: 'Atenção!',
          description:
            'Esta página estará disponível somente durante o período de execução do evento'
        })
      }
    }
  }, [disallowedModules, modules, router, digitalProductId, isAllowedModule])

  return [isAllowedModule, byPass, status, startAt, endAt]
}
